/**
 * WelcomeTour — a lightweight, dependency-free onboarding overlay.
 *
 * Used after a guest visitor signs up and lands on their newly-claimed
 * schedule (see LoginPage / GoogleCallbackPage: navigate adds
 * `?welcome_tour=1`). Walks them through the most useful things they
 * can do next: upload logos, enter scores, share, export.
 *
 * Design goals:
 *  * Zero external deps — pure React + tailwind.
 *  * Never crashes if a target selector goes missing (e.g., mobile hides
 *    a button) — just skips that step.
 *  * Only shows once per user per browser (localStorage flag).
 *
 * The parent decides *when* to open the tour and passes `steps` — each
 * step is { title, body, selector }.
 */
import React, { useEffect, useState, useCallback, useLayoutEffect } from 'react';
import { X, ChevronLeft, ChevronRight, CheckCircle2 } from 'lucide-react';

const PADDING = 10;      // px of space around the highlighted element
const TOOLTIP_GAP = 14;  // px between the highlight and the tooltip

function getTargetRect(selector) {
  if (!selector) return null;
  const el = document.querySelector(selector);
  if (!el) return null;
  // If the target is hidden (display:none) rect will be 0x0 — treat as missing.
  const rect = el.getBoundingClientRect();
  if (rect.width === 0 && rect.height === 0) return null;
  // Scroll target into view (best-effort) so a step near the bottom of a
  // long page still shows something.
  try { el.scrollIntoView({ behavior: 'smooth', block: 'center', inline: 'nearest' }); } catch { /* ignore */ }
  return rect;
}

function computeTooltipPosition(rect) {
  const vw = typeof window !== 'undefined' ? window.innerWidth : 1024;
  const vh = typeof window !== 'undefined' ? window.innerHeight : 768;
  const tooltipW = Math.min(340, vw - 32);
  const tooltipH = 200; // estimated; we don't measure to avoid a re-render loop
  if (!rect) {
    // Centered — welcome/finish steps.
    return {
      left: Math.max(16, (vw - tooltipW) / 2),
      top: Math.max(16, (vh - tooltipH) / 2),
      width: tooltipW,
      arrow: null,
    };
  }
  // Prefer placing the tooltip *below* the target; if not enough room, place above.
  const spaceBelow = vh - rect.bottom;
  const spaceAbove = rect.top;
  let top;
  let arrow;
  if (spaceBelow >= tooltipH + TOOLTIP_GAP || spaceBelow >= spaceAbove) {
    top = rect.bottom + TOOLTIP_GAP;
    arrow = 'top';
  } else {
    top = Math.max(16, rect.top - tooltipH - TOOLTIP_GAP);
    arrow = 'bottom';
  }
  // Horizontal — try to center on the target, then clamp to viewport.
  let left = rect.left + rect.width / 2 - tooltipW / 2;
  left = Math.max(16, Math.min(left, vw - tooltipW - 16));
  return { left, top, width: tooltipW, arrow };
}

/**
 * @param {object}   props
 * @param {boolean}  props.open
 * @param {Array<{title: string, body: string, selector?: string}>} props.steps
 * @param {() => void} props.onClose
 * @param {string}   props.storageKey — localStorage flag to mark tour as seen.
 */
export default function WelcomeTour({ open, steps = [], onClose, storageKey }) {
  const [index, setIndex] = useState(0);
  const [rect, setRect] = useState(null);
  const [tick, setTick] = useState(0); // force position recompute on resize/scroll

  const step = steps[index];
  const total = steps.length;
  const isFirst = index === 0;
  const isLast = index === total - 1;

  const finish = useCallback(() => {
    if (storageKey && typeof window !== 'undefined') {
      try { localStorage.setItem(storageKey, '1'); } catch { /* private mode */ }
    }
    onClose && onClose();
  }, [storageKey, onClose]);

  // Reset to step 0 whenever the tour is re-opened
  useEffect(() => { if (open) setIndex(0); }, [open]);

  // Recompute the highlight rect whenever the step changes, or on
  // resize/scroll so the overlay follows the user.
  useLayoutEffect(() => {
    if (!open || !step) return;
    setRect(getTargetRect(step.selector));
  }, [open, step, tick]);

  useEffect(() => {
    if (!open) return;
    const handler = () => setTick(t => t + 1);
    window.addEventListener('resize', handler);
    window.addEventListener('scroll', handler, true);
    // Poll a few times for the first ~500ms so slow-loading layouts settle.
    const polls = [50, 150, 300, 500].map(ms => setTimeout(handler, ms));
    return () => {
      window.removeEventListener('resize', handler);
      window.removeEventListener('scroll', handler, true);
      polls.forEach(clearTimeout);
    };
  }, [open, index]);

  // Keyboard shortcuts
  useEffect(() => {
    if (!open) return;
    const handler = (e) => {
      if (e.key === 'Escape') { finish(); }
      else if (e.key === 'ArrowRight' || e.key === 'Enter') {
        if (isLast) finish(); else setIndex(i => Math.min(total - 1, i + 1));
      } else if (e.key === 'ArrowLeft') {
        setIndex(i => Math.max(0, i - 1));
      }
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [open, isLast, total, finish]);

  if (!open || !step) return null;

  const pos = computeTooltipPosition(rect);
  const highlightStyle = rect ? {
    left: rect.left - PADDING,
    top: rect.top - PADDING,
    width: rect.width + PADDING * 2,
    height: rect.height + PADDING * 2,
  } : null;

  return (
    <div className="fixed inset-0 z-[9999]" data-testid="welcome-tour" aria-modal="true" role="dialog">
      {/* Backdrop with a punched-out hole around the highlighted element */}
      {rect ? (
        <>
          {/* Four rectangles that surround the highlight — avoids relying on SVG mask */}
          <div className="absolute bg-black/60" style={{ left: 0, top: 0, width: '100%', height: highlightStyle.top }} onClick={finish} />
          <div className="absolute bg-black/60" style={{ left: 0, top: highlightStyle.top + highlightStyle.height, width: '100%', bottom: 0 }} onClick={finish} />
          <div className="absolute bg-black/60" style={{ left: 0, top: highlightStyle.top, width: highlightStyle.left, height: highlightStyle.height }} onClick={finish} />
          <div className="absolute bg-black/60" style={{ left: highlightStyle.left + highlightStyle.width, top: highlightStyle.top, right: 0, height: highlightStyle.height }} onClick={finish} />
          {/* Highlight border */}
          <div
            className="absolute rounded-lg ring-4 ring-[#FFD700] ring-offset-2 ring-offset-transparent pointer-events-none animate-pulse"
            style={highlightStyle}
            data-testid="welcome-tour-highlight"
          />
        </>
      ) : (
        <div className="absolute inset-0 bg-black/60" onClick={finish} />
      )}

      {/* Tooltip card */}
      <div
        className="absolute bg-white rounded-xl shadow-2xl p-5 border border-slate-200"
        style={{ left: pos.left, top: pos.top, width: pos.width }}
        data-testid="welcome-tour-tooltip"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-start justify-between gap-3 mb-2">
          <div className="flex items-center gap-2">
            <span className="text-[11px] font-bold uppercase tracking-widest text-[#00247D]/60">
              Step {index + 1} of {total}
            </span>
          </div>
          <button
            type="button"
            onClick={finish}
            className="text-slate-400 hover:text-slate-600 transition-colors"
            aria-label="Skip tour"
            data-testid="welcome-tour-skip"
          >
            <X className="w-4 h-4" />
          </button>
        </div>
        <h3 className="text-lg font-extrabold text-[#00247D] leading-tight" style={{ fontFamily: 'Barlow Condensed' }} data-testid="welcome-tour-title">
          {step.title}
        </h3>
        <p className="mt-2 text-sm text-slate-600 leading-relaxed" data-testid="welcome-tour-body">
          {step.body}
        </p>
        <div className="mt-4 flex items-center justify-between">
          <button
            type="button"
            onClick={() => setIndex(i => Math.max(0, i - 1))}
            disabled={isFirst}
            className="inline-flex items-center gap-1 text-xs font-semibold text-slate-500 hover:text-[#00247D] disabled:opacity-30 disabled:cursor-not-allowed"
            data-testid="welcome-tour-back"
          >
            <ChevronLeft className="w-4 h-4" />
            Back
          </button>
          <div className="flex items-center gap-1">
            {steps.map((_, i) => (
              <span
                key={i}
                className={`w-1.5 h-1.5 rounded-full ${i === index ? 'bg-[#00247D]' : 'bg-slate-300'}`}
              />
            ))}
          </div>
          {isLast ? (
            <button
              type="button"
              onClick={finish}
              className="inline-flex items-center gap-1 bg-[#00247D] hover:bg-[#001A5C] text-white text-xs font-bold px-3 py-1.5 rounded-lg transition-colors"
              data-testid="welcome-tour-finish"
            >
              <CheckCircle2 className="w-4 h-4" />
              Got it
            </button>
          ) : (
            <button
              type="button"
              onClick={() => setIndex(i => Math.min(total - 1, i + 1))}
              className="inline-flex items-center gap-1 bg-[#00247D] hover:bg-[#001A5C] text-white text-xs font-bold px-3 py-1.5 rounded-lg transition-colors"
              data-testid="welcome-tour-next"
            >
              Next
              <ChevronRight className="w-4 h-4" />
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
